import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link2, Check, Share2 } from 'lucide-react'
import { toast } from '../lib/toast'

interface Props {
  url?: string        // absolutní URL ke sdílení (default = aktuální stránka)
  title?: string
  className?: string
}

// Oficiální značková loga (simple-icons paths) — Lucide brand ikony nemá.
const XIcon = () => (
  <svg viewBox="0 0 24 24" className="w-[18px] h-[18px]" fill="currentColor" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
)
const FbIcon = () => (
  <svg viewBox="0 0 24 24" className="w-[18px] h-[18px]" fill="currentColor" aria-hidden="true">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
  </svg>
)
const InIcon = () => (
  <svg viewBox="0 0 24 24" className="w-[18px] h-[18px]" fill="currentColor" aria-hidden="true">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
)

/** Sdílecí lišta — X, Facebook, LinkedIn (reálná loga), kopírovat odkaz. */
export default function ShareBar({ url, title, className = '' }: Props) {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)
  const shareUrl = url || (typeof window !== 'undefined' ? window.location.href : '')
  const text = title || (typeof document !== 'undefined' ? document.title : 'SEO Tools')
  const u = encodeURIComponent(shareUrl)
  const tx = encodeURIComponent(text)

  const shareLabel = (net: string) => `${t('share.label', { defaultValue: 'Sdílet' })} ${net}`
  const links = [
    { name: 'X', href: `https://twitter.com/intent/tweet?url=${u}&text=${tx}`, Icon: XIcon, hover: 'hover:bg-black hover:border-black hover:text-white' },
    { name: 'Facebook', href: `https://www.facebook.com/sharer/sharer.php?u=${u}`, Icon: FbIcon, hover: 'hover:bg-[#1877f2] hover:border-[#1877f2] hover:text-white' },
    { name: 'LinkedIn', href: `https://www.linkedin.com/sharing/share-offsite/?url=${u}`, Icon: InIcon, hover: 'hover:bg-[#0a66c2] hover:border-[#0a66c2] hover:text-white' },
  ]

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl)
      setCopied(true)
      toast.success(t('share.copied', { defaultValue: 'Odkaz zkopírován' }))
      setTimeout(() => setCopied(false), 2000)
    } catch { /* ignore */ }
  }

  return (
    <div className={`flex items-center gap-2 flex-wrap ${className}`}>
      <span className="inline-flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 mr-1">
        <Share2 className="w-4 h-4" />{t('share.label', { defaultValue: 'Sdílet' })}
      </span>
      {links.map((l) => (
        <a key={l.name} href={l.href} target="_blank" rel="noopener noreferrer" aria-label={shareLabel(l.name)} title={shareLabel(l.name)}
          className={`inline-flex items-center justify-center w-9 h-9 rounded-full border border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-300 transition cursor-pointer ${l.hover}`}>
          <l.Icon />
        </a>
      ))}
      <button onClick={copy} aria-label={t('share.copy_link', { defaultValue: 'Kopírovat odkaz' })} title={t('share.copy_link', { defaultValue: 'Kopírovat odkaz' })}
        className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10 transition cursor-pointer">
        {copied ? <Check className="w-[18px] h-[18px] text-emerald-500" /> : <Link2 className="w-[18px] h-[18px]" />}
      </button>
    </div>
  )
}
